import { useEffect, useState } from 'react'
import { dataLayer } from '../../data/dataLayer'
import type { Account, AccountsState } from '../../accounts'
import type { ModuleData, ModuleId } from '../../content'

/**
 * useHomeBanners — SSOT boundary for the learner-home preference reads
 * (App-Feel 2027 W21 / V-AF-04). Previously `HomePage` imported `dataLayer`
 * directly and ran two ad-hoc async effects in the view. This hook owns the
 * data access so the page stays a pure presentation surface.
 *
 * It encapsulates two independent one-shot preference flows:
 *  1. `firstFocusSubject` — the subject the learner picked during onboarding;
 *     surfaced once as a CTA, then cleared so it never re-fires.
 *  2. The klascode-banner dismissal flag + the "no classroom yet" check.
 */
export interface UseHomeBannersResult {
  /** The onboarding-picked module to surface, or null when nothing to show. */
  firstFocusModuleId: ModuleId | null
  /** Clears the persisted `firstFocusSubject` and hides the CTA. */
  clearFirstFocus: () => Promise<void>
  /** Whether the "heb je een klascode?" banner should render. */
  showKlascodeBanner: boolean
  /** Persists the dismissal and hides the klascode banner. */
  dismissKlascodeBanner: () => Promise<void>
}

export function useHomeBanners(
  activeAccount: Account | null | undefined,
  accounts: AccountsState | null | undefined,
  modules: ModuleData[],
): UseHomeBannersResult {
  // ── 1. First-focus subject (one-time onboarding handover) ────────────────
  const [firstFocusModuleId, setFirstFocusModuleId] = useState<ModuleId | null>(
    null,
  )
  useEffect(() => {
    if (!activeAccount || activeAccount.role !== 'learner') return
    let cancelled = false
    dataLayer
      .getLearnerPreferences()
      .then((prefs) => {
        if (cancelled) return
        const id = prefs.firstFocusSubject as ModuleId | undefined
        if (!id) return
        // Only surface if it's actually a known module for this learner.
        const known = modules.some((m) => m.id === id)
        if (known) setFirstFocusModuleId(id)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [activeAccount, modules])

  const clearFirstFocus = async () => {
    setFirstFocusModuleId(null)
    try {
      const prefs = await dataLayer.getLearnerPreferences()
      const { firstFocusSubject: _omit, ...rest } = prefs
      await dataLayer.saveLearnerPreferences(rest as typeof prefs)
    } catch {
      // Non-fatal — the in-memory state is already cleared.
    }
  }

  // ── 2. Klascode banner (dismissal flag + no-classroom check) ─────────────
  const [showKlascodeBanner, setShowKlascodeBanner] = useState(false)
  useEffect(() => {
    let cancelled = false
    async function checkKlascodeBanner() {
      if (!activeAccount || activeAccount.role !== 'learner') {
        if (!cancelled) setShowKlascodeBanner(false)
        return
      }
      // Check if banner was dismissed
      const dismissed = await dataLayer.getKlascodeBannerDismissed()
      if (cancelled) return
      if (dismissed) {
        setShowKlascodeBanner(false)
        return
      }
      // Check if learner is in any classroom
      const hasClassroom =
        !!accounts &&
        Object.values(accounts.classrooms || {}).some((classroom) =>
          classroom.learnerIds.includes(activeAccount.id),
        )
      if (!cancelled) setShowKlascodeBanner(!hasClassroom)
    }
    checkKlascodeBanner()
    return () => {
      cancelled = true
    }
  }, [activeAccount, accounts])

  const dismissKlascodeBanner = async () => {
    await dataLayer.setKlascodeBannerDismissed(true)
    setShowKlascodeBanner(false)
  }

  return {
    firstFocusModuleId,
    clearFirstFocus,
    showKlascodeBanner,
    dismissKlascodeBanner,
  }
}
